import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import EventRow from "../components/EventRow";
import { formatEventDates } from "../utils/dateUtils";
import { OperaContext } from "../context/OperaContext";
import { SeasonNoticeAndNewsletter } from "../components/SeasonNoticeAndNewsletter";
import Breadcrumb from "../components/Breadcrumb";
import { useTranslation } from "react-i18next";

// Utility to normalize URLs (add your own logic as needed)
function normalizeUrl(url) {
  if (!url) return "";
  // If already absolute, return as is
  if (/^(https?:)?\/\//.test(url)) return url;
  // Otherwise, prepend origin (for SSR, you may want to adjust this)
  return `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`;
}

function ComposerPage() {
  const { composerName } = useParams();
  const { loading } = useContext(OperaContext);
  const [composer, setComposer] = useState(null);
  const [events, setEvents] = useState([]);
  const [loadingComposer, setLoadingComposer] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  // Smart image positioning based on aspect ratio
  const handleSmartImageLoad = (e) => {
    const img = e.target;
    const ratio = img.naturalWidth / img.naturalHeight;

    // Only apply special positioning for tall portrait images (ratio < 1)
    if (ratio < 1) {
      const className = img.className;
      if (className.includes("h-[600px]") || className.includes("h-full")) {
        img.style.objectPosition = "center 8%"; // Hero images
      } else if (className.includes("h-[154px]")) {
        img.style.objectPosition = "center 50%"; // Thumbnails
      }
    }
    // Square and landscape images keep default center positioning
  };

  useEffect(() => {
    if (loading) return;
    setLoadingComposer(true);

    // Fetch composer by slug directly
    axios
      .get(`/api/composers/${composerName}`)
      .then((res) => {
        setComposer(res.data);

        // Now fetch events for this composer
        return axios
          .get(`/api/events?composers=${res.data._id}`)
          .then((eventsRes) => {
            setEvents(eventsRes.data);
          });
      })
      .catch((err) => {
        setError(err.message || "An error occurred");
      })
      .finally(() => {
        setLoadingComposer(false);
      });
  }, [composerName, loading]);

  // Show loading state while context is loading or composer data is being fetched
  if (loading || loadingComposer) {
    return (
      <div className="container mx-auto text-center text-black py-20">
        {t("ComposersPage.loading")}
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto text-center text-black py-20">
        {t("ComposersPage.error")}: {error}
      </div>
    );
  }

  if (!composer) {
    return (
      <div className="container mx-auto text-center text-black py-20">
        {t("ComposersPage.composerNotFound")}
      </div>
    );
  }

  const heroImage =
    composer.coverImage || composer.image || "/images/default-performer.jpg";

  return (
    <div className="bg-white">
      {/* Schema.org Person JSON-LD for Composer */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            (() => {
              const data = {
                "@context": "https://schema.org",
                "@type": "Person",
                name: composer.name,
              };
              if (composer.image) data.image = normalizeUrl(composer.image);
              if (composer.description) data.description = composer.description;
              return data;
            })()
          ),
        }}
      />
      <div className="relative w-full h-[600px] overflow-hidden">
        <img
          src={heroImage}
          alt={composer.name}
          className="w-full h-full object-cover object-top"
          onLoad={handleSmartImageLoad}
        />
      </div>
      <Breadcrumb
        items={[
          { href: "/", label: "Home" },
          {
            href: "/composers",
            label: "Composers",
          },
          {
            href: `#`,
            label: composer.name,
          },
        ]}
      />
      <main className="max-w-[1280px] mx-auto py-8 px-4">
        <h1 className="text-3xl text-left mb-4 capitalize">
          {composer.name}
        </h1>
        <div className="flex flex-col md:flex-row md:gap-8 mb-8">
          <div className="text-lg text-black leading-relaxed md:flex-1 md:text-justify md:w-[78%]" dangerouslySetInnerHTML={{ __html: composer?.description }} />
          <div className="hidden md:block md:flex-1 md:max-w-[23%]">
            {composer.image ? (
              <img
                src={composer.image}
                alt={composer.name}
                className="w-[260px] h-[260px] object-cover"
                style={{ objectPosition: "center 50%" }}
                onLoad={handleSmartImageLoad}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div className="w-[260px] h-[260px] bg-gray-100 flex items-center justify-center text-gray-500 text-base rounded-lg"></div>
            )}
          </div>
        </div>
        <section>
          <h2 className="text-2xl text-left mb-4">
            {t("ComposersPage.upcomingPerformances")}
          </h2>
          {events.length === 0 ? (
            <p className="text-black text-center italic py-8">
              {t("ComposersPage.hero")}
            </p>
          ) : (
            <div className="flex flex-col gap-2 max-w-[1240px] mx-auto my-6">
              {events.map((event) => (
                <EventRow
                  key={event._id}
                  event={event}
                  formatEventDates={formatEventDates}
                  page="composer"
                  pageImage={composer?.image}
                />
              ))}
            </div>
          )}
          <SeasonNoticeAndNewsletter />
        </section>
      </main>
    </div>
  );
}

export default ComposerPage;
